import React, { useEffect, useState, useCallback } from 'react';
import { ArrowLeft, MessageSquare, Gamepad2, Brain, Image, Video, Clock, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function GetStartedPage() {
  const navigate = useNavigate();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const features = [
    {
      title: "Fitness Website Example",
      description: "Complete fitness tracking and workout planning platform template",
      path: "https://fitnesssiteexample.escapingthematrix.io",
      backgroundImage: "/images/fitness_site.webp",
      fallbackImage: "/images/fitness_site.jpg",
      customContent: true,
      group: "business"
    },
    {
      title: "Sales Website Example",
      description: "E-commerce platform with advanced sales tracking and analytics",
      path: "https://salesiteexample.escapingthematrix.io",
      backgroundImage: "/images/sales_photo.webp",
      fallbackImage: "/images/sales_photo.jpg",
      customContent: true,
      customStyle: "sales",
      group: "business"
    },
    {
      title: "Game Example",
      description: "Interactive browser-based game implementations and templates",
      path: "https://gameexample1.escapingthematrix.io",
      backgroundImage: "/images/flappy_bird.webp",
      fallbackImage: "/images/flappy_bird.jpg",
      customContent: true,
      customStyle: "game",
      group: "games"
    },
    {
      icon: <Gamepad2 className="w-8 h-8" />,
      title: "Game Example 2",
      description: "Another interactive game demo showcasing different mechanics",
      path: "https://gameexample2.escapingthematrix.io",
      backgroundImage: "/images/snake_game.png",
      fallbackImage: "/images/snake_game.jpg",
      customContent: true,
      customStyle: "game2",
      group: "games"
    },
    {
      icon: <MessageSquare className="w-8 h-8" />,
      title: "Chat Help Bot Examples",
      description: "Interactive chatbot templates and implementations for customer support",
      path: "https://chatbotsiteexample.escapingthematrix.io",
      backgroundImage: "/images/Chat_Now.webp",
      fallbackImage: "/images/Chat_Now.jpg",
      customContent: true,
      customStyle: "chat",
      group: "chat"
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: "AI Website Integration",
      description: "Seamless AI integration examples for modern web applications",
      path: "https://AIsiteexample.escapingthematrix.io",
      backgroundImage: "/images/Explore_AI.webp",
      fallbackImage: "/images/Explore_AI.jpg",
      customContent: true,
      customStyle: "ai",
      group: "chat"
    },
    {
      icon: <Image className="w-8 h-8" />,
      title: "AI Generated Images",
      description: "Advanced AI image generation and manipulation examples",
      path: "https://aigeneratedimages.escapingthematrix.io",
      backgroundImage: "/images/Wall.gif?v=1",
      customContent: true,
      customStyle: "ai",
      group: "media"
    },
    {
      icon: <Video className="w-8 h-8" />,
      title: "AI Generated Videos",
      description: "Cutting-edge AI video generation and editing examples",
      path: "https://aigeneratedvideos.escapingthematrix.io",
      backgroundImage: "/images/AI_Video.gif?v=1",
      customContent: true,
      customStyle: "ai",
      group: "media"
    }
  ];

  const comingSoonFeatures = [
    {
      icon: <Brain className="w-8 h-8" />,
      title: "AI-Powered Blog/Content",
      description: "AI-powered content generation and management",
      path: "https://contentai.escapingthematrix.io/",
      backgroundImage: "/images/content_ai.webp",
      fallbackImage: "/images/content_ai.jpeg",
      customContent: true,
      customStyle: "ai"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Membership & Subscription Management",
      description: "Complete membership and subscription management system",
      path: "https://membershipmanage.escapingthematrix.io/",
      backgroundImage: "/images/membership_management.webp",
      fallbackImage: "/images/membership_management.jpeg",
      customContent: true,
      customStyle: "business"
    }
  ];

  const groupDescriptions = {
    business: "From high-converting sales funnels to fully automated fitness platforms, we build websites that work as hard as you do. Whether you need seamless user onboarding, integrated AI support, or streamlined backend systems, we deliver fast, flexible, and future-ready solutions.",
    games: "Want to launch addictive browser-based games as we can help you craft immersive, replayable experiences designed to engage and retain your audience — fast.",
    chat: "Say goodbye to repetitive customer questions. Our AI chatbots deliver instant, intelligent support around the clock — learning from every interaction to better serve your users. Plus, with deep integration into your site, they enhance user experience and free up your time.",
    media: "Elevate your content strategy with AI-generated visuals that wow. Instantly create high-quality images and dynamic videos tailored to your brand — perfect for marketing, storytelling, and standing out in a crowded digital world.",
    comingSoon: "Say goodbye to content chaos and manual membership management. Our AI-powered blog and subscription platform helps you create and publish optimized content effortlessly while building and managing member communities with automated billing, onboarding, and content delivery."
  };

  const handleNavigation = (url: string) => {
    if (url !== "#") {
      window.location.href = url;
    }
  };

  const renderFeatureGroup = (features: any[], index: number) => {
    const groupType = features[0].group;
    return (
      <div key={index} className="space-y-6">
        <div className="grid grid-cols-2 gap-4 md:gap-6 lg:gap-8 max-w-7xl mx-auto">
          {features.map((feature, featureIndex) => (
            <div 
              key={featureIndex}
              className={`p-4 md:p-6 rounded-lg border border-green-400/30 hover:border-green-400 
                       transition-all duration-300 bg-black/50 cursor-pointer relative overflow-hidden
                       ${feature.customContent ? 'h-48 md:h-64' : ''} ${feature.path === '#' ? 'opacity-75' : ''}`}
              onClick={() => handleNavigation(feature.path)}
            >
              {feature.customContent ? (
                <>
                  <picture>
                    {feature.fallbackImage && <source srcSet={feature.backgroundImage} type="image/webp" />}
                    <img 
                      src={feature.fallbackImage || feature.backgroundImage}
                      alt={feature.title}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </picture>
                  <div className={`absolute inset-0 ${
                    feature.customStyle === 'sales' 
                      ? 'bg-gradient-to-r from-blue-900/40 to-transparent'
                      : feature.customStyle === 'game' || feature.customStyle === 'game2'
                      ? 'bg-gradient-to-t from-black/80 via-black/50 to-transparent'
                      : feature.customStyle === 'chat'
                      ? 'bg-gradient-to-r from-black/60 via-black/40 to-transparent'
                      : feature.customStyle === 'ai'
                      ? 'bg-gradient-to-r from-black/60 via-black/40 to-transparent'
                      : feature.customStyle === 'business'
                      ? 'bg-gradient-to-r from-black/60 via-black/40 to-transparent'
                      : 'bg-black bg-opacity-40'
                  }`}></div>
                  <div className="relative z-10">
                    <p className="text-xs sm:text-sm font-medium text-white mb-8">{feature.title}</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-green-400 mb-4">{feature.icon}</div>
                  <h3 className="text-lg sm:text-xl font-semibold mb-2 text-green-400">{feature.title}</h3>
                  <p className="text-sm sm:text-base text-gray-400">{feature.description}</p>
                </>
              )}
            </div>
          ))}
        </div>
        {(groupType && groupDescriptions[groupType]) && (
          <p className="text-base sm:text-lg text-gray-300 text-center px-4 max-w-4xl mx-auto">
            {groupDescriptions[groupType]}
          </p>
        )}
      </div>
    );
  };

  const groupedFeatures = features.reduce((acc, feature) => {
    if (!feature.group) return acc;
    if (!acc[feature.group]) acc[feature.group] = [];
    acc[feature.group].push(feature);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-black text-white">
      <nav className="fixed top-0 w-full px-6 py-4 bg-black bg-opacity-70 backdrop-blur-sm z-50">
        <button 
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-white hover:text-green-400 transition-colors duration-300"
        >
          <ArrowLeft size={20} />
          Back to Main Page
        </button>
      </nav>

      <div className="container mx-auto px-4 md:px-6 pt-24 pb-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center space-y-2 sm:space-y-3 md:space-y-4 mb-12 md:mb-16">
            <h1 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold tracking-wider glitch-text text-green-400 whitespace-nowrap">
              ESCAPE THE MATRIX
            </h1>
            <h1 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold tracking-wider glitch-text text-green-400 whitespace-nowrap">
              BUILD WITH AI
            </h1>
            <h1 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold tracking-wider glitch-text text-green-400 whitespace-nowrap">
              SCALE WITH AUTOMATION
            </h1>
          </div>
          
          <div className="space-y-8 md:space-y-12 mb-16 text-center">
            <div className="space-y-6 md:space-y-8">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white leading-tight">
                EscapingTheMatrix: Your Launchpad for Digital Freedom
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-white/80 leading-relaxed">
                Empowering creators, entrepreneurs, and forward-thinking businesses to thrive in a fast-moving digital world.
              </p>
              <div className="space-y-4 text-sm sm:text-base md:text-lg text-gray-300 leading-relaxed">
                <p>
                  We specialize in building high-performance websites, intelligent web apps, lead generation tools, and AI chatbots — all tailored to your specific vision. Whether you're launching a brand, streamlining operations, or scaling an existing business, we deliver results quickly and efficiently.
                </p>
                
                <p>
                  Our solutions are built on three pillars: speed, customization, and automation. From responsive landing pages to complex multi-functional platforms, we craft digital systems that eliminate bottlenecks and unlock new growth opportunities.
                </p>
                
                <p>
                  At the core of our work is intelligent automation. We believe digital systems should work for you 24/7, not demand constant maintenance. That's why we design everything to scale effortlessly, reduce repetitive tasks, and improve user experience.
                </p>
                
                <p>
                  With EscapingTheMatrix, you're not just getting a website. You're building a powerful, future-ready ecosystem that enhances productivity, multiplies your impact, and frees you to focus on what matters most.
                </p>
                
                <p className="text-green-400 font-semibold">
                  Let's build smarter, launch faster, and escape the limits of outdated tech — together.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-12 mb-12">
            {Object.entries(groupedFeatures).map(([group, features], index) => 
              renderFeatureGroup(features, index)
            )}
            
            <div className="grid grid-cols-2 gap-4 md:gap-6 lg:gap-8 max-w-7xl mx-auto">
              {comingSoonFeatures.map((feature, index) => (
                <div 
                  key={`platform-${index}`}
                  className={`p-4 md:p-6 rounded-lg border border-green-400/30 hover:border-green-400 
                           transition-all duration-300 bg-black/50 cursor-pointer relative overflow-hidden
                           h-48 md:h-64`}
                  onClick={() => handleNavigation(feature.path)}
                >
                  <picture>
                    {feature.fallbackImage && <source srcSet={feature.backgroundImage} type="image/webp" />}
                    <img 
                      src={feature.fallbackImage || feature.backgroundImage}
                      alt={feature.title}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </picture>
                  <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-transparent"></div>
                  <div className="relative z-10">
                    <p className="text-xs sm:text-sm font-medium text-white mb-8">{feature.title}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-base sm:text-lg text-gray-300 text-center px-4 max-w-4xl mx-auto">
              {groupDescriptions.comingSoon}
            </p>
          </div>

          <div className="space-y-6 md:space-y-8 text-left">
            <section>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-white">Quick Start Guide</h2>
              <div className="bg-gray-900 rounded-lg p-4 sm:p-6 font-mono text-sm sm:text-base">
                <p className="text-green-400 mb-2"># Install the Matrix AI CLI</p>
                <code className="block text-white mb-4">Coming Soon</code>
                
                <p className="text-green-400 mb-2"># Initialize a new project</p>
                <code className="block text-white mb-4">Coming Soon</code>
                
                <p className="text-green-400 mb-2"># Start the development server</p>
                <code className="block text-white">Coming Soon</code>
              </div>
            </section>

            <section>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 flex items-center gap-2 text-white">
                Next Steps <span className="text-xl sm:text-2xl">🚀</span>
              </h2>
              <div className="space-y-4 sm:space-y-6 text-sm sm:text-base text-gray-300">
                <div className="flex items-start gap-2">
                  <span className="text-green-400">✅</span>
                  <div>
                    <span>Connect with Our Community: Join us on </span>
                    <a 
                      href="https://x.com/EscapeMatrixAI" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="font-bold text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      X
                    </a>
                    <span> and </span>
                    <a 
                      href="https://t.me/AIEscapingtheMatrix" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="font-bold text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      Telegram
                    </a>
                    <span> to stay in the loop.</span>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-400">📌</span>
                  <div>
                    <button
                      onClick={scrollToTop}
                      className="text-left hover:text-gray-400 transition-colors"
                    >
                      Explore & Get Inspired: Browse example projects and templates to kickstart your journey.
                    </button>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-400">📩</span>
                  <div>
                    Stay Updated: Subscribe to our newsletter for the latest updates – Coming Soon!
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GetStartedPage;